import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import type { SessionExpiryDialogComponent } from '../../components/session-expiry-dialog/session-expiry-dialog.component';

/** Cuánto antes de caducar se renueva el token en silencio (2 min). */
const ANTICIPACION_SILENCIOSA_MS = 120_000;
/** Cuenta atrás máxima del aviso cuando no se conoce la caducidad exacta. */
const AVISO_MAX_SEGUNDOS = 120;

/**
 * Mantiene viva la sesión con la pestaña abierta: renueva el access token en
 * silencio antes de que caduque y, solo si la renovación falla, avisa al
 * usuario con una cuenta atrás en lugar de expulsarlo de golpe.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private authService = inject(AuthService);
  private dialog = inject(Dialog);
  private router = inject(Router);

  /** Segundos restantes que muestra el diálogo de aviso. */
  readonly remainingSeconds = signal(0);
  /** Indica si hay una renovación en curso (deshabilita botones). */
  readonly renewing = signal(false);

  private dialogRef: DialogRef<unknown, SessionExpiryDialogComponent> | null = null;
  private closedSub: Subscription | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | undefined;
  private countdownTimer: ReturnType<typeof setInterval> | undefined;
  private started = false;

  constructor() {
    // Reprograma la renovación silenciosa cada vez que cambia el token o el estado
    effect(() => {
      this.authService.token();
      this.authService.authStatus();
      this.reprogramar();
    });
  }

  /** Arranca los listeners globales (llamar una vez desde el inicializador). */
  start(): void {
    if (this.started) return;
    this.started = true;
    window.addEventListener('visibilitychange', this.alVolverVisible);
    window.addEventListener('storage', this.alCambiarStorage);
    this.reprogramar();
  }

  /** Reintento manual desde el diálogo ("Seguir conectado"). */
  async keepAlive(): Promise<void> {
    await this.intentarRenovacion();
  }

  /** Cierre manual desde el diálogo. */
  logoutNow(): void {
    this.limpiarTodo();
    this.authService.logout();
  }

  /** Programa (o ejecuta ya) la renovación silenciosa según la caducidad. */
  private reprogramar(): void {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = undefined;
    if (this.authService.authStatus() !== 'authenticated') {
      this.cerrarAviso();
      return;
    }
    const ms = this.msHastaCaducidad();
    if (ms === null) return;
    if (ms <= ANTICIPACION_SILENCIOSA_MS) {
      // Dentro del margen (o caducado): intenta renovar ya; si falla, avisa
      void this.intentarRenovacion();
      return;
    }
    this.refreshTimer = setTimeout(() => {
      void this.intentarRenovacion();
    }, ms - ANTICIPACION_SILENCIOSA_MS);
  }

  /** Intenta renovar; solo muestra el aviso si la renovación falla. */
  private async intentarRenovacion(): Promise<void> {
    if (this.renewing()) return;
    if (this.authService.authStatus() !== 'authenticated') return;
    this.renewing.set(true);
    try {
      const ok = await firstValueFrom(this.authService.refreshToken());
      if (ok) {
        this.cerrarAviso();
        this.reprogramar();
      } else {
        await this.mostrarAviso();
      }
    } catch {
      await this.mostrarAviso();
    } finally {
      this.renewing.set(false);
    }
  }

  /** Abre el aviso con cuenta atrás (importación diferida, sin ciclo de imports). */
  private async mostrarAviso(): Promise<void> {
    if (this.dialogRef) return;
    if (this.router.url.startsWith('/auth')) return;
    this.actualizarRestantes();
    if (this.remainingSeconds() <= 0) {
      this.logoutNow();
      return;
    }
    const { SessionExpiryDialogComponent } = await import(
      '../../components/session-expiry-dialog/session-expiry-dialog.component'
    );
    // La sesión pudo cerrarse mientras se cargaba el diálogo
    if (this.authService.authStatus() !== 'authenticated') return;
    if (this.dialogRef) return;
    this.dialogRef = this.dialog.open(SessionExpiryDialogComponent, { disableClose: true });
    const ref = this.dialogRef;
    this.closedSub = ref.closed.subscribe(() => {
      this.dialogRef = null;
    });
    clearInterval(this.countdownTimer);
    this.countdownTimer = setInterval(() => {
      this.actualizarRestantes();
      if (this.remainingSeconds() <= 0) this.logoutNow();
    }, 1000);
  }

  /** Cierra el aviso sin cerrar la sesión (la renovación funcionó). */
  private cerrarAviso(): void {
    clearInterval(this.countdownTimer);
    this.countdownTimer = undefined;
    this.closedSub?.unsubscribe();
    this.closedSub = null;
    this.dialogRef?.close();
    this.dialogRef = null;
    this.remainingSeconds.set(0);
  }

  /** Limpia temporizadores y aviso (al cerrar sesión). */
  private limpiarTodo(): void {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = undefined;
    this.cerrarAviso();
  }

  /** Recalcula los segundos restantes desde la caducidad real del token. */
  private actualizarRestantes(): void {
    const ms = this.msHastaCaducidad();
    if (ms === null) {
      this.remainingSeconds.update((actual) => (actual > 0 ? actual - 1 : AVISO_MAX_SEGUNDOS));
    } else {
      this.remainingSeconds.set(Math.max(0, Math.ceil(ms / 1000)));
    }
  }

  /** Milisegundos hasta que caduque el access token (null si se desconoce). */
  private msHastaCaducidad(): number | null {
    const expira = this.authService.getAccessTokenExpiry();
    if (expira === null) return null;
    return expira - Date.now();
  }

  /** Al volver a la pestaña, renueva si queda poco (los timers se pausan ocultos). */
  private alVolverVisible = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (this.authService.authStatus() !== 'authenticated') return;
    const ms = this.msHastaCaducidad();
    if (ms !== null && ms < ANTICIPACION_SILENCIOSA_MS + 60_000) void this.intentarRenovacion();
  };

  /** Sincroniza multipestaña: otra pestaña renovó (nuevo token) o cerró sesión. */
  private alCambiarStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== 'token' && event.key !== 'refreshToken') return;
    this.authService.reloadTokensFromStorage();
    if (this.authService.authStatus() === 'not-authenticated' && !this.router.url.startsWith('/auth')) {
      this.limpiarTodo();
      this.router.navigate(['/auth/login']);
    }
  };
}
